import { FC, HTMLAttributes, useState } from "react";
import styled, { css } from "styled-components";
import { Payouts } from "components/Payouts";
import { AnimButton, Center, Header } from "utils/constants";
import { useStore } from "utils/store";
import { useCurrency, useTournamentDetails } from "hooks";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useOrientation } from "hooks/orientation";
import { useLayout } from "components/Layout";
import { useWindowFocus } from 'hooks/useWindowFocus';
import { Video } from "components/Video";
import { MediaQuery } from "types";
import { useSkinnedAssets } from "hooks/skin";
import { useTranslation } from "react-i18next";
import { parseNumber } from "utils/number";

const Container = styled(Center)<{ $bgDesktop: string, $bgMobile: string }>`
  width: 100%;
  height: 100%;
  position: absolute;
  background: radial-gradient(circle 37.5vw at left bottom, rgba(126, 72, 30, 0.6), rgba(0, 0, 0, 0) 72%), url(${props => props.$bgDesktop}) right center / auto 100% no-repeat;

  @media (orientation: landscape) and (${MediaQuery.ACR}) {
    background: url(${props => props.$bgDesktop}) right center / auto 100% no-repeat;
  }

  @media (orientation: portrait) {
    background: url(${props => props.$bgMobile}) left center / auto 100% no-repeat;
  }
`;

const Hr = styled.hr`
  width: 96%;
  border-top: 1px solid #d29b75;
  opacity: 0.5;
  margin: 1vh auto;
`;

const Logo = styled.div<{ $logo: string }>`
  background: url(${props => props.$logo}) center / contain no-repeat;
  width: 24.6vw;
  height: 26.2vh;
  position: relative;
  margin: 0 auto;

  @media (orientation: landscape) and (height < 640px) {
    width: 16vw;
    height: 28vh;
    background-position: right bottom;
    position: fixed;
    right: 0.4vw;
    bottom: 0;
  }

  @media (orientation: portrait) {
    width: 76vw;
    height: 27vh;
  }
`;

const Buy = styled(Center)`
  position: relative;
  z-index: 1;
  width: clamp(35rem, 18.012rem + 57.831vh, 50rem);
  margin: 0 auto;
  color: #fff;
  text-align: center;
  flex-direction: column;

  & > ${Hr} {
    margin: 0.5vh auto;
  }

  @media (orientation: portrait) {
    width: 89vw;
  }
`;

const Text = styled.div`
  margin: 2.2vh auto 4vh;
  font-size: clamp(1.319rem, 0.638rem + 2.18vh, 2rem);
  line-height: 0.86;
  letter-spacing: clamp(0.047rem, 0.027rem + 0.064vh, 0.067rem);
  font-weight: normal;
  text-shadow: 0 0 4px #000, 0 0 8px #000;

  @media (orientation: portrait) {
    margin: 2vh auto 4vh;
    font-size: clamp(1.125rem, 0rem + 5vw, 2rem);
    text-shadow: none;
  } 
`;

const FlexText = styled(Text)`
  margin: 1vh auto 2vh;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (orientation: landscape){
    & > span:not(:last-child)::after {
      content: '\\0000a0';
    }
  }

  @media (orientation: portrait) {
    margin-top: 2.9vh;
    flex-direction: column;
  } 
`;

const Link = styled.span`
  color: #ffd797;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  &:active {
    color: #fff;
  }
`;

const BuyIns = styled(Center)`
  width: 100%;
  height: clamp(3.188rem, 0.022rem + 10.13vh, 5.625rem);
  flex-direction: row;

  @media (orientation: portrait) {
    height: 6.4vh;
  } 
`;

const Price = styled.div`
  width: 200%;
  margin-top: 6%;
  text-shadow: 0 2px 0 #4e4e57;
  text-align: center;
  color: #cfcfcf;
  font-weight: 700;
  font-size: clamp(2.419rem, 0rem + 7.74vh, 4.281rem);
  line-height: 1;
  position: relative;

  &::first-letter {
    font-weight: 600;
    font-size: 80%;
  }

  @media (orientation: portrait) {
    font-size: min(4.5vh, 3.5rem);
  } 
`;

const Transition = styled(AnimButton)`
  overflow: hidden;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: #fff;
    opacity: 0;
  }

  &:active::after {
    opacity: 0.9;
  }
`;

const hover = () => {
  return css`
    text-shadow: none;
    filter: drop-shadow(0 1px 0 #fff);
    background-image: linear-gradient(
      to bottom,
      #000 28%,
      rgba(87, 84, 84, 0.82) 41%,
      #000 56%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  `;
};

const Active = styled(Transition)<{ $active: boolean }>`
  font-family: Teko;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (orientation: landscape) {
    &:hover ${Price}, &:hover${Text} { ${hover()} }
  }

  & ${Price}, ${Text} {
    ${(props) => props.$active && hover()}
  }
`;

const buttonHover = () => {
  return css`
    border: solid 1px #fff;
    box-shadow: 0 0 9px 0 #fff, inset 0 2px 0 0 #fff;
    background-image: linear-gradient(to bottom, #898a9f 0%, #fbfbfc 52%, #828399 100%);

    @media (orientation: portrait) {
      box-shadow: 0 0 15px 0 #fff, inset 0 0.3vh 0 0 #fff;
    } 
  `;
};

const Button = styled(Active)`
  width: clamp(5.688rem, 0.006rem + 18.182vh, 10.063rem);
  height: 100%;
  margin: 0 clamp(0.5rem, 0.217rem + 0.964vh, 0.75rem);
  outline: none;
  border-radius: 8px;
  box-sizing: border-box;
  border: solid 1px #737375;
  box-shadow: inset 0 2px 0 0 #7e7e7e;
  background-image: linear-gradient(to bottom, #484852 0%, #7b7b7c 52%, #42434e 100%);
  pointer-events: ${props => props.$active ? "none" : "auto"};

  ${(props) => props.$active && buttonHover()}

  @media (orientation: portrait) {
    width: 11.7vh;
    border-radius: 1vh;
  }

  @media (orientation: landscape) {
    &:hover { ${buttonHover()} }
  } 
`;

const Play = styled(Active)`
  width: clamp(14.3rem, -0.002rem + 45.766vh, 25.313rem);
  height: clamp(6.688rem, -0.001rem + 21.403vh, 11.838rem);
  margin: 4vh auto;
  border-radius: 11px;
  border: solid 2px #82764b;
  background-image: linear-gradient(to bottom, #6b5233 0%, #827e6b 52%, #6b5132 100%);
  box-shadow: 0 -4px 20px 0 rgba(255, 155, 0, 0.5), 0 -4px 0 0 #4d2e05;
  box-sizing: border-box;
  text-align: center;
  color: #3c3120;
  flex-direction: column;
  pointer-events: none;

  & ${Text} {
    font-size: clamp(2.288rem, 0rem + 7.32vh, 4.049rem);
    font-weight: 700;
    margin: 0 auto 0;
    letter-spacing: normal;
    line-height: 1;
    text-shadow: 0 2px 0 rgba(255, 255, 255, 0.2);
  }

  & ${Price} {
    font-size: clamp(3.525rem, 0rem + 11.28vh, 6.239rem);
    color: #6d6d6d;
    text-shadow: none;
    margin-top: -1.5%;
    line-height: 1;
  }

  @media (orientation: portrait) {
    width: calc(min(17vh, 10.125rem) * 2.25);
    height: min(17vh, 10.125rem);
    box-shadow: 0 0.5vh 2.9vh 0 #ff9b00, 0 0.9vh 0 0 #985b09;
    border-radius: 1.4vh;

    & ${Text} {
      font-size: min(6.8vh, 3.5rem);
    }

    & ${Price} {
      font-size: min(10vh, 5.25rem);
    }
  }

  ${(props) => {
    if(props.$active){
      return css`
        pointer-events: auto;
        border-color: #ffe893;
        box-shadow: 0 -4px 20px 0 #ff9b00, 0 -4px 0 0 #985b09;
        background-image: linear-gradient(to bottom, #cf9b5e 0%, #fff8d3 51%, #cf9b5e 100%);

        & ${Text} {
          margin-top: 5%;
        }
      `;
    }
  }}
`;

const Hook = styled.div`
  --height: clamp(1.625rem, 0.634rem + 3.373vh, 2.5rem);

  width: clamp(20.75rem, 6.735rem + 47.711vh, 33.125rem);
  height: var(--height);
  border-radius: clamp(0.375rem, 0.092rem + 0.964vh, 0.625rem);
  box-shadow: 0 0 9px 0 rgba(255, 255, 255, 0.79);
  border: solid 1px #fff;
  background-image: linear-gradient(to bottom, #898a9f 0%, #fbfbfc 52%, #828399 100%);
  position: relative;
  text-shadow: 0 1px 0 #fff;
  font-size: clamp(1.25rem, 0.401rem + 2.892vh, 2rem);
  font-weight: 500;
  letter-spacing: 0.71px;
  line-height: 1;
  text-align: center;
  color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: calc(var(--height) * 0.32);
    height: calc(var(--height) * 0.32);
    background-color: #fff;
    transform: rotate(45deg);
    top: calc((100% - (var(--height) * 0.32)) / 2);
    z-index: -1;
  }

  &::before {
    left: calc(var(--height) * -0.16);
  }

  &::after {
    right: calc(var(--height) * -0.16);
  }

  @media (orientation: landscape) and (${MediaQuery.ACR}) {
    margin-bottom: 13px;
  }
`;

const HeaderContainer = styled.div`
  margin: 1.75vh auto 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (orientation: portrait) {
    margin-top: 0;
    flex-direction: column;
  } 
`;

export const Lobby: FC<HTMLAttributes<HTMLDivElement>> = () => {
  useWindowFocus();
  const [showPayouts, setShowPayouts] = useState(false);
  const { parseCurrency } = useCurrency();
  const { buyIn, buyIns } = useTournamentDetails();
  const setBuyIn = useStore((state) => state.setBuyIn);
  const play = useSoundStore(state => state.play);
  const { portrait } = useOrientation();
  const { isReady, onPlay } = useLayout();
  const { bgDesktop, bgMobile, logo } = useSkinnedAssets();
  const { t } = useTranslation();

  return (
    <Container $bgDesktop={bgDesktop} $bgMobile={bgMobile}>
      {isReady &&
        <>
          <Buy>
            {
              !portrait && (
                <Hook>{t('Win up to {{ value }}X+ on a single scratch card', { value: parseNumber(10000) })}</Hook>
              )
            }
            <Logo $logo={logo} data-testid="bounty-hunter-logo" />
            <HeaderContainer>
              <Header>{t('POKER')}</Header>
              <Header>{t('SCRATCH CARDS')}</Header>
            </HeaderContainer>
            <FlexText><span>{t('PAYOUTS BASED ON REAL, CERTIFIED,')}</span><span>{t('HISTORICAL AI POKER TOURNAMENTS')}</span></FlexText>
            <HeaderContainer>
              <Header>{t('ONE HERO AI PER CARD -')}</Header>
              <Header>{t('WIN WHAT THEY WON!')}</Header>
            </HeaderContainer>
            {!portrait && <Hr />}
            <Text>
              {t('MORE EXPENSIVE CARDS HAVE HIGHER')}&nbsp;
              <Link onClick={() => setShowPayouts(true)}>{t('BOUNTIES')}</Link>
            </Text>
            <BuyIns>
              {[...buyIns].sort((a, b) => a.buyIn - b.buyIn).map((value, index) => (
                <Button
                  key={index}
                  $active={buyIn?.buyIn === value.buyIn}
                  onMouseDown={() => play(Sounds.MouseClick)}
                  onClick={() => setBuyIn(value)}
                  data-testid={`lobby-buy-in-${value.buyIn}-button`}
                >
                  <Price>{parseCurrency(value.buyIn)}</Price>
                </Button>
              ))}
            </BuyIns>
            <Play 
              $active={!!buyIn.buyIn} 
              onClick={() => {
                onPlay(false);
              }}
              onMouseDown={() => play(Sounds.MouseClick)}
              data-testid="play-now-button"
            >
              <Text>{t('PLAY NOW')}</Text>
              {!!buyIn.buyIn && <Price>{parseCurrency(buyIn.buyIn)}</Price>}
            </Play>
          </Buy>
          <Payouts show={showPayouts} onClose={() => setShowPayouts(false)} />
          {!portrait && <Video />}
        </>
      }
    </Container>
  );
};
